import { ClientSelect } from '../../core/client-select';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';
import {
  ClientCredential,
  ClientCredentialFilterValue,
  ClientCredentialsApi,
  CreateClientCredentialRequest
} from '../../core/client-credentials-api';

type Action = 'create' | 'update' | 'disable';

@Component({
  selector: 'app-client-credentials',
  imports: [CommonModule, FormsModule, PageNavigation, ClientSelect],
  templateUrl: './client-credentials.html'
})
export class ClientCredentials implements OnInit {
  rows: ClientCredential[] = [];
  filters: Record<string, ClientCredentialFilterValue> = {};
  searchText = '';
  page = 0;
  size = 10;
  total = 0;
  readonly pageSizes = [10, 25, 50, 100];

  form: CreateClientCredentialRequest = this.emptyForm();
  editing?: ClientCredential;
  modalOpen = false;
  createdSecret = '';
  tableLoading = false;
  loadingAction?: Action;
  message = '';
  error = '';

  constructor(
    private readonly api: ClientCredentialsApi,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  get isEditing(): boolean {
    return Boolean(this.editing);
  }

  get canSave(): boolean {
    return Boolean(this.form.clienteId && this.form.razonSocial.trim() && this.form.cuit);
  }

  openCreate(): void {
    this.form = this.emptyForm();
    this.editing = undefined;
    this.createdSecret = '';
    this.message = '';
    this.error = '';
    this.modalOpen = true;
  }

  openEdit(row: ClientCredential): void {
    this.form = {
      clienteId: row.clienteId ?? null,
      razonSocial: row.razon_social ?? '',
      cuit: row.cuit ?? null
    };
    this.editing = row;
    this.createdSecret = '';
    this.message = '';
    this.error = '';
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editing = undefined;
    this.createdSecret = '';
  }

  save(): void {
    if (!this.canSave || !this.form.clienteId) {
      return;
    }

    if (this.isEditing) {
      this.update();
      return;
    }

    this.create();
  }

  disable(row: ClientCredential): void {
    if (!row.clienteId || !row.enabled) {
      return;
    }

    this.begin('disable');
    this.api
      .disableByClienteId(row.clienteId)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Credencial deshabilitada.';
          this.load();
        },
        error: error => this.handleError(error, 'No se pudo deshabilitar la credencial.')
      });
  }

  copySecret(): void {
    if (!this.createdSecret || !navigator.clipboard) {
      return;
    }

    void navigator.clipboard.writeText(this.createdSecret);
    this.message = 'Secret copiado.';
  }

  load(): void {
    this.tableLoading = true;
    this.api
      .getData({ page: this.page, size: this.size, search: this.searchText, filters: this.cleanFilters() })
      .pipe(finalize(() => {
        this.tableLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: response => {
          this.rows = response.data ?? [];
          this.total = response.recordsFiltered ?? response.recordsTotal ?? 0;
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo cargar la tabla de credenciales.')
      });
  }

  applyFilters(): void {
    this.page = 0;
    this.load();
  }

  clearFilters(): void {
    this.filters = {};
    this.searchText = '';
    this.page = 0;
    this.load();
  }

  changePage(nextPage: number): void {
    if (nextPage < 0 || nextPage >= this.totalPages || nextPage === this.page) {
      return;
    }
    this.page = nextPage;
    this.load();
  }

  changeSize(): void {
    this.page = 0;
    this.load();
  }

  private create(): void {
    this.begin('create');
    this.api
      .create(this.form)
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: credential => {
          this.createdSecret = credential.cognitoClientSecret ?? '';
          this.message = 'Credenciales creadas correctamente.';
          this.load();
          this.cdr.markForCheck();
        },
        error: error => this.handleError(error, 'No se pudo crear el client credential.')
      });
  }

  private update(): void {
    if (!this.form.clienteId) {
      return;
    }

    this.begin('update');
    this.api
      .update(this.form.clienteId, { razonSocial: this.form.razonSocial, cuit: this.form.cuit })
      .pipe(finalize(() => this.finish()))
      .subscribe({
        next: () => {
          this.message = 'Credencial actualizada.';
          this.closeModal();
          this.load();
        },
        error: error => this.handleError(error, 'No se pudo actualizar la credencial.')
      });
  }

  private begin(action: Action): void {
    this.loadingAction = action;
    this.message = '';
    this.error = '';
  }

  private finish(): void {
    this.loadingAction = undefined;
    this.cdr.markForCheck();
  }

  private handleError(error: unknown, fallback: string): void {
    console.error(fallback, error);
    this.error = this.errorMessage(error, fallback);
    this.cdr.markForCheck();
  }

  private cleanFilters(): Record<string, ClientCredentialFilterValue> {
    return Object.entries(this.filters).reduce<Record<string, ClientCredentialFilterValue>>((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        acc[key] = value;
      } else if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'object' && error && 'error' in error) {
      const body = (error as {
        error?: {
          errors?: Array<{ description?: string; message?: string; detail?: string }>;
          message?: string;
          detail?: string;
          error?: string;
        };
      }).error;
      const descriptions = body?.errors
        ?.map(item => item.description ?? item.message ?? item.detail)
        .filter((description): description is string => Boolean(description));
      if (descriptions?.length) {
        return descriptions.join('\n');
      }
      return body?.message ?? body?.detail ?? body?.error ?? fallback;
    }
    return fallback;
  }

  private emptyForm(): CreateClientCredentialRequest {
    return { clienteId: null, razonSocial: '', cuit: null };
  }
}
